<template>
	<div class="timetables-editor">
		<ModalHeader
			:title="title"
			:button-text="getString('save')"
			@click-button="clickSave" />
		<div class="timetables-container">
			<AppList :items="items" >
				<template v-slot:timetable-custom="{item, index}">
					<div class="list-item-header">
						<div class="main-header-content">
							<p class="list-item-title">{{ item.title }}</p>
							<div v-for="timetable in item.timetables" class="timetable-container">
								<div class="timetable">
									<div v-if="withQuantity && timetable.hasOwnProperty('quantity') && timetable.quantity > 0" class="timetable-quantity">{{ timetable.quantity }}</div>
									<p>{{ getTimetableString(timetable) }}</p>
									<AppClickableIcon icon-name="iconX" class="button-remove" :auto-set-active="false" @click-icon="deleteTimetable(timetable)" />
								</div>
							</div>
						</div>
						<div class="header-controls">
							<AppButtonIcon icon-name="icon-copy" prevent-change small @click-icon="copyTimetables(item)" />
							<AppButtonIcon :ref="'button-add-timetable-'+index" icon-name="icon-plus-white" :active="!!item.active" @click-icon="openAddTimetable(index)" small  />
						</div>
					</div>
				</template>
				<template v-slot:timetable-dropdown="{item, index}">
					<div class="new-timetable-container">
						<AppList :items="timetablesItems(index)" />
					</div>
				</template>
			</AppList>
		</div>
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppButton from "@/shared/components/AppButton.vue";

import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUserStore} from "@/console-new/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, menuStore};
	},
	emits: ["save-clicked"],
	name: "TimetablesEditor",
	components: {AppButton, AppNumberInput, AppTextInput, AppButtonIcon, AppList, AppBottomSheet, ModalHeader, AppClickableIcon},
	props: {
		title: {
			type: String,
			required: true
		},
		timetables: {
			type: Array,
			required: true,
			validator(value) {
				return value.every(t => t.hasOwnProperty("week_day") && t.hasOwnProperty("hour_start") && t.hasOwnProperty("minute_start") && t.hasOwnProperty("hour_end") && t.hasOwnProperty("minute_end"));
			}
		},
		withQuantity: {
			type: Boolean,
			default: false
		},
		withIntervals: {
			type: Boolean,
			default: false
		},
		menuItemId: {
			type: Number,
			default: null
		},
		menuCategoryId: {
			type: Number,
			default: null
		},
		delivery: {
			type: Boolean,
			default: false
		},
		takeaway: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			currentTimetables: this.timetables,
			actives: [
				false,
				false,
				false,
				false,
				false,
				false,
				false,
			],
			fromTime: "",
			toTime: "",
			quantity: 0,
			intervals: 0,
		};
	},
	computed: {
		items() {
			return [
				{title: this.getString("monday"), week_day: 0, switch: true, active: this.actives[0], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(0, this.currentTimetables)},
				{title: this.getString("tuesday"), week_day: 1, switch: true, active: this.actives[1], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(1, this.currentTimetables)},
				{title: this.getString("wednesday"), week_day: 2, switch: true, active: this.actives[2], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(2, this.currentTimetables)},
				{title: this.getString("thursday"), week_day: 3, switch: true, active: this.actives[3], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(3, this.currentTimetables)},
				{title: this.getString("friday"), week_day: 4, switch: true, active: this.actives[4], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(4, this.currentTimetables)},
				{title: this.getString("saturday"), week_day: 5, switch: true, active: this.actives[5], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(5, this.currentTimetables)},
				{title: this.getString("sunday"), week_day: 6, switch: true, active: this.actives[6], customSlot: "timetable-custom", dropdownSlot: "timetable-dropdown", timetables: this.getTimetables(6, this.currentTimetables)}
			];
		}
	},
	methods: {
		timetablesItems(weekIndex) {
			let items = [
				{
					title: this.getString("from"),
					textInput: true,
					inputType: "time",
					callbackText: v => { this.fromTime = v; },
				},{
					title: this.getString("to"),
					textInput: true,
					inputType: "time",
					callbackText: v => { this.toTime = v; },
				}
			]
			if(this.withQuantity) {
				items.push({
					title: this.getString("quantity"),
					numberInput: true,
					min: 0,
					max: 999,
					callbackNumber: v => { this.quantity = v; },
				});
			}
			if(this.withIntervals) {
				items.push({
					title: this.getString("intervals"),
					numberInput: true,
					value: 15,
					min: 1,
					max: 120,
					symbol: 'm',
					rightSymbol: true,
					callbackNumber: v => { this.intervals = v; },
				});
			}
			items.push({
				buttonTitle: this.getString("add"),
				callbackButton: () => {
					this.addTimetable(weekIndex);
				},
			});
			return items;
		},
		getTimetables(week_day, timetables) {
			return timetables.filter(t => t.week_day === week_day);
		},
		getTimetableString(timetable) {
			return (timetable.hour_start < 10 ? "0"+timetable.hour_start : timetable.hour_start)
				+":"+(timetable.minute_start < 10 ? "0"+timetable.minute_start : timetable.minute_start)
				+" - "+(timetable.hour_end < 10 ? "0"+timetable.hour_end : timetable.hour_end)
				+":"+(timetable.minute_end < 10 ? "0"+timetable.minute_end : timetable.minute_end);
		},
		deleteTimetable(timetable) {
			let timetableToDeletedIndex = this.currentTimetables.findIndex(
				t => t.hour_start === timetable.hour_start && t.minute_start === timetable.minute_start && t.hour_end === timetable.hour_end && t.minute_end === timetable.minute_end && t.week_day === timetable.week_day
			);
			this.currentTimetables.splice(timetableToDeletedIndex, 1);
		},
		addTimetable(index) {
			let timeFrom = this.fromTime;
			let timeTo = this.toTime;
			let quantity = this.withQuantity ? this.quantity : null;
			let intervals = this.withIntervals ? this.intervals : null;
			if (timeFrom && timeTo) {
				let hourStart = parseInt(timeFrom.split(":")[0]);
				let minuteStart = parseInt(timeFrom.split(":")[1]);
				let hourEnd = parseInt(timeTo.split(":")[0]);
				let minuteEnd = parseInt(timeTo.split(":")[1]);

				let newTimetable = {hour_start: hourStart, minute_start: minuteStart, hour_end: hourEnd, minute_end: minuteEnd, week_day: index};
				let secondTimetable = null;

				//Double timetable check
				if(hourStart > hourEnd && hourStart >= 12 && hourEnd <= 12) {
					secondTimetable = {hour_start: 0, minute_start: 0, hour_end: hourEnd, minute_end: minuteEnd, week_day: (index + 1) % 7};
					newTimetable.hour_end = 23;
					newTimetable.minute_end = 59;
				}

				let checkResult = this.checkValidTimetable(newTimetable);
				let checkResult2 = secondTimetable ? this.checkValidTimetable(secondTimetable) : "valid";
				if (checkResult === "valid" && checkResult2 === "valid") {
					if (this.withQuantity) {
						newTimetable.quantity = quantity && quantity > 0 ? quantity : null;
						if (secondTimetable) {
							secondTimetable.quantity = quantity && quantity > 0 ? quantity : null;
						}
					}
					if(this.withIntervals){
						newTimetable.intervals = intervals > 0 ? intervals : 15;
						if (secondTimetable) {
							secondTimetable.intervals = intervals > 0 ? intervals : 15;
						}
					}
					this.currentTimetables.push(newTimetable);
					if (secondTimetable) {
						this.currentTimetables.push(secondTimetable);
					}
					this.orderTimetables();
					this.fromTime = "";
					this.toTime = "";
					this.quantity = 0;
					this.intervals = 1;

				} else if(checkResult === "invalid" || checkResult2 === "invalid") {
					this.showNotification("timetable-invalid");
				} else if(checkResult === "overlap" || checkResult2 === "overlap") {
					this.showNotification("timetable-overlaps");
				} else {
					this.showNotification("generic-fail");
				}
			} else {
				this.showNotification("timetable-invalid-time");
			}
		},
		checkValidTimetable(timetable) {
			if(timetable.hour_start < timetable.hour_end || (timetable.hour_start === timetable.hour_end && timetable.minute_start < timetable.minute_end)) {
				let weekdayTimetable = this.items.find(item => item.week_day === timetable.week_day).timetables;
				for(let i = 0; i < weekdayTimetable.length; i++) {
					let t = weekdayTimetable[i];
					if (t.hour_start < timetable.hour_end || (t.hour_start === timetable.hour_end && t.minute_start < timetable.minute_end)) {
						if (t.hour_end > timetable.hour_start || (t.hour_end === timetable.hour_start && t.minute_end > timetable.minute_start)) {
							return "overlap";
						}
					}
				}

				return "valid";
			} else {
				return "invalid";
			}
		},
		copyTimetables(item) {
			this.currentTimetables = this.currentTimetables.filter(t => t.week_day === item.week_day);
			let itemTimetables = item.timetables;
			for(let i = 0; i < 7; i++) {
				if (i !== item.week_day) {
					this.currentTimetables = this.currentTimetables.concat(itemTimetables.map(t => {
						let newTimetable = {hour_start: t.hour_start, minute_start: t.minute_start, hour_end: t.hour_end, minute_end: t.minute_end, week_day: i};
						if (this.withQuantity) {
							newTimetable.quantity = t.quantity;
						}
						if (this.withIntervals) {
							newTimetable.intervals = t.intervals;
						}
						return newTimetable;
					}));
				}
			}
		},
		orderTimetables() {
			this.timetables.sort((a, b) => {
				if (a.week_day === b.week_day) {
					if (a.hour_start === b.hour_start) {
						return a.minute_start - b.minute_start;
					} else {
						return a.hour_start - b.hour_start;
					}
				} else {
					return a.week_day - b.week_day;
				}
			});
		},
		openAddTimetable(index) {
			for(let i = 0; i < this.actives.length; i++) {
				if(i !== index) {
					this.actives[i] = false;
					this.$refs["button-add-timetable-"+i].isActive = false;
				} else {
					this.actives[i] = !this.actives[i];
					this.$refs["button-add-timetable-"+i].isActive = this.actives[i];
				}
			}
		},
		clickSave() {
			let self = this;
			if(this.menuItemId) {
				this.menuStore.updateItemTimetables(this.menuItemId, this.currentTimetables, function() {
					self.showNotification("timetable-saved");
					self.$emit("save-clicked");
				});
			} else if(this.menuCategoryId) {
				this.menuStore.updateCategoryTimetables(this.menuCategoryId, this.currentTimetables, function() {
					self.showNotification("timetable-saved");
					self.$emit("save-clicked");
				});
			} else if(this.delivery) {
				this.userStore.updateDeliveryTimetables(this.currentTimetables, function() {
					self.showNotification("timetable-saved");
					self.$emit("save-clicked");
				});
			} else if(this.takeaway) {
				this.userStore.updateTakeawayTimetables(this.currentTimetables, function() {
					self.showNotification("timetable-saved");
					self.$emit("save-clicked");
				});
			}
		},
	},
	mounted() {
		this.orderTimetables();
	},
	watch: {
		timetables(newTimetables) {
			this.timetables = newTimetables;
		}
	}
}
</script>

<style lang="scss" scoped>

.timetables-container {
	padding: 15px 0;

	.list-item-header {
		display: flex;
		justify-content: space-between;
		align-items: stretch;
		width: 100%;

		.main-header-content {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-start;
			align-items: center;
			width: calc(100% - 55px);

			.list-item-title {
				min-width: 110px;
				min-height: 35px;
				display: flex;
				align-items: center;
				padding-right: 15px;
			}

			.timetable-container {
				display: flex;
				align-items: center;
				padding-right: 8px;
				margin-top: 4px;
				margin-bottom: 4px;
				margin-left: 8px;

				.timetable {
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding: 2px 8px 2px 12px;
					border-radius: 13px;
					background-color: var(--color-bg-primary-2);
					font-size: 14px;
					user-select: none;
					position: relative;

					p {
						font-size: 14px;
						color: var(--color-txt-1);
					}

					.button-remove {
						margin-left: 5px;
						padding: 2px;
						display: flex;
						align-items: center;
					}

					.timetable-quantity {
						position: absolute;
						top: -3px;
						right: calc(100% - 9px);
						background-color: var(--color-primary);
						color: var(--color-bg);
						font-size: 11px;
						padding: 1px 5px;
						border-radius: 13px;

					}
				}
			}
		}

		.header-controls {
			display: flex;
			justify-content: space-between;
			align-items: center;
			width: 55px;

		}
	}

	.new-timetable-container {
		display: flex;
		justify-content: space-between;
		width: 100%;

		.new-timetable-half {
			display: flex;
			width: 50%;

			.input-wrapper {
				width: 120px;
			}

			&.left {
				justify-content: space-between;
			}

			&.right {
				padding-left: 20px;
				align-items: center;
				justify-content: flex-end;

				.quantity-input {
					margin-right: 20px;
				}
			}

			&.right.quantity {
				align-items: flex-end;
			}

			@media (max-width: 1080px) {
				flex-direction: column;

				&.left {
					align-items: flex-start;
				}

				&.right {
					justify-content: center;
					align-items: flex-end;

					.quantity-input {
						margin-right: 0;
					}
				}

				&.right.quantity {
					justify-content: space-between;
				}

				.input-wrapper:first-child {
					margin-bottom: 20px;
				}
			}
		}
	}
}

</style>